import * as THREE from 'three';
import type { ItemDef } from '../content/types';
import { primitiveGeometry } from '../render/geometry';
import type { ModelLibrary } from '../render/models';

/** Compact procedural 3D showroom used by the Equipment tab. */
export class ItemPreview {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(30, 1, 0.1, 30);
  private readonly clock = new THREE.Clock();
  private readonly stage = new THREE.Group();
  private readonly ownedGeometry: THREE.BufferGeometry[] = [];
  private frame = 0;
  private everConnected = false;
  private targetYaw = -0.35;
  private yaw = -0.35;
  private dragging = false;
  private pointerX = 0;
  private item: ItemDef | null = null;

  constructor(private readonly canvas: HTMLCanvasElement, private readonly models?: ModelLibrary) {
    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.25;
    this.camera.position.set(0, 0.7, 4.4);
    this.camera.lookAt(0, 0.55, 0);
    this.scene.add(this.stage);
    this.scene.add(new THREE.HemisphereLight(0xd9f8ff, 0x071018, 2.4));
    const key = new THREE.DirectionalLight(0xffffff, 4.5);
    key.position.set(-3, 4, 4);
    const rim = new THREE.DirectionalLight(0x56e7ff, 5.2);
    rim.position.set(4, 2, -3);
    this.scene.add(key, rim);
    const platform = this.mesh(new THREE.CylinderGeometry(0.92, 1.08, 0.12, 48), new THREE.MeshStandardMaterial({ color: 0x0b1a23, metalness: 0.7, roughness: 0.24, emissive: 0x073744, emissiveIntensity: 0.45 }));
    platform.position.y = -0.34;
    this.scene.add(platform);
    const ring = this.mesh(new THREE.TorusGeometry(0.96, 0.016, 8, 72), new THREE.MeshBasicMaterial({ color: 0x62e5ff, transparent: true, opacity: 0.72 }));
    ring.rotation.x = Math.PI / 2;
    ring.position.y = -0.27;
    this.scene.add(ring);

    canvas.addEventListener('pointerdown', (event) => { this.dragging = true; this.pointerX = event.clientX; canvas.setPointerCapture(event.pointerId); });
    canvas.addEventListener('pointermove', (event) => { if (!this.dragging) return; this.targetYaw += (event.clientX - this.pointerX) * 0.012; this.pointerX = event.clientX; });
    const stop = () => { this.dragging = false; };
    canvas.addEventListener('pointerup', stop);
    canvas.addEventListener('pointercancel', stop);
    this.frame = requestAnimationFrame(() => this.animate());
  }

  setItem(item: ItemDef): void {
    this.item = item;
    for (const child of [...this.stage.children]) {
      this.stage.remove(child);
      const mesh = child as THREE.Mesh;
      const materials = mesh.material ? (Array.isArray(mesh.material) ? mesh.material : [mesh.material]) : [];
      for (const material of materials) material.dispose();
    }
    const visual = item.visual ?? { geo: item.slot === 'relic' ? 'octa' as const : item.slot === 'armor' ? 'box' as const : 'capsule' as const, color: 0x62e5ff };
    const usingModel = !!visual.gltf && !!this.models?.has(visual.gltf);
    const material = () => usingModel
      ? this.models!.materialFor(visual)
      : new THREE.MeshStandardMaterial({ color: visual.color, roughness: item.slot === 'armor' ? 0.52 : 0.3, metalness: item.slot === 'armor' ? 0.35 : 0.58, emissive: visual.emissive ?? visual.color, emissiveIntensity: visual.emissiveIntensity ?? 0.08, flatShading: true });
    const hero = new THREE.Mesh(usingModel ? this.models!.geometryFor(visual) : primitiveGeometry(visual.geo), material());
    hero.scale.setScalar(item.slot === 'relic' ? 1.15 : 1.4);
    hero.scale.y *= visual.scaleY ?? 1;
    hero.position.y = 0.58;
    this.stage.add(hero);
    if (item.slot === 'weapon') {
      hero.rotation.z = -0.72;
      hero.scale.set(0.78, 0.78, 1.75);
      const grip = this.mesh(new THREE.CylinderGeometry(0.09, 0.12, 1.45, 10), new THREE.MeshStandardMaterial({ color: 0x4d3427, roughness: 0.7, metalness: 0.1 }));
      grip.position.set(-0.42, 0.12, 0);
      grip.rotation.z = -0.72;
      this.stage.add(grip);
    } else if (item.slot === 'armor') {
      hero.scale.set(1.3, 1.5, 0.72);
      for (const x of [-0.68, 0.68]) {
        const shoulder = this.mesh(new THREE.SphereGeometry(0.27, 8, 6), material());
        shoulder.position.set(x, 0.8, 0);
        this.stage.add(shoulder);
      }
    } else {
      const orbit = this.mesh(new THREE.TorusGeometry(0.72, 0.035, 8, 42), new THREE.MeshBasicMaterial({ color: visual.emissive ?? visual.color, transparent: true, opacity: 0.72 }));
      orbit.position.y = 0.58;
      orbit.rotation.x = Math.PI / 2.8;
      this.stage.add(orbit);
    }
    this.targetYaw = -0.35;
    this.yaw = -0.9;
  }

  private mesh(geometry: THREE.BufferGeometry, material: THREE.Material): THREE.Mesh {
    this.ownedGeometry.push(geometry);
    return new THREE.Mesh(geometry, material);
  }

  private animate(): void {
    if (this.canvas.isConnected) this.everConnected = true;
    if (this.everConnected && !this.canvas.isConnected) return this.dispose();
    const dt = Math.min(this.clock.getDelta(), 0.05);
    const t = this.clock.elapsedTime;
    this.yaw += (this.targetYaw - this.yaw) * Math.min(1, dt * 8);
    const anim = this.item?.visual?.anim ?? 'bob';
    this.stage.rotation.y = this.yaw + (anim === 'spin' ? t * 0.45 : 0);
    this.stage.position.y = anim === 'none' ? 0 : Math.sin(t * 2.1) * 0.045;
    const pulse = anim === 'pulse' ? 1 + Math.sin(t * 3.4) * 0.035 : 1;
    this.stage.scale.setScalar(pulse);
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (this.canvas.width !== Math.round(width * dpr) || this.canvas.height !== Math.round(height * dpr)) {
      this.renderer.setPixelRatio(dpr);
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    }
    this.renderer.render(this.scene, this.camera);
    this.frame = requestAnimationFrame(() => this.animate());
  }

  dispose(): void {
    cancelAnimationFrame(this.frame);
    this.scene.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.material) return;
      for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) material.dispose();
    });
    for (const geometry of this.ownedGeometry) geometry.dispose();
    this.renderer.dispose();
  }
}
